import * as hardware from "@client/hardware";
import React from "react";

const isTextInput = (element: Element | null) =>
    element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

type SoftwareKeyboard = {
    open: boolean;
    // Pixels to lift a bottom-docked element by so it sits on the keyboard.
    // Zero on native: the Capacitor resize mode is `native`, so the webview
    // itself shrinks and bottom 0 is already the keyboard's top edge.
    inset: number;
};

const CLOSED: SoftwareKeyboard = {open: false, inset: 0};

// Reports the software keyboard. Native shells report it through the Capacitor
// keyboard events; on the web the visual viewport shrinks under a focused text
// input, which is the only signal a browser gives.
export const useSoftwareKeyboard = () => {
    const [keyboard, setKeyboard] = React.useState<SoftwareKeyboard>(CLOSED);

    React.useEffect(() => {
        let cancelled = false;
        let cleanup: (() => void) | null = null;

        void hardware.getAppInfo().then(({platform}) => {
            if (cancelled) {
                return;
            }
            if (platform === "web") {
                const viewport = window.visualViewport;
                if (viewport == null) {
                    return;
                }
                const onResize = () => {
                    const delta = window.innerHeight - viewport.height;
                    setKeyboard(
                        delta > 150 && isTextInput(document.activeElement)
                            ? {open: true, inset: delta}
                            : CLOSED,
                    );
                };
                viewport.addEventListener("resize", onResize);
                document.addEventListener("focusout", onResize);
                cleanup = () => {
                    viewport.removeEventListener("resize", onResize);
                    document.removeEventListener("focusout", onResize);
                };
                return;
            }
            const show = hardware.Keyboard.addListener("keyboardWillShow", () =>
                setKeyboard({open: true, inset: 0}),
            );
            const hide = hardware.Keyboard.addListener("keyboardWillHide", () =>
                setKeyboard(CLOSED),
            );
            cleanup = () => {
                void show.then((handle) => handle.remove());
                void hide.then((handle) => handle.remove());
            };
        });

        return () => {
            cancelled = true;
            cleanup?.();
        };
    }, []);

    React.useEffect(() => {
        if (!keyboard.open) {
            return;
        }
        const timer = window.setTimeout(() => {
            const active = document.activeElement;
            if (active instanceof HTMLElement) {
                active.scrollIntoView({block: "center", behavior: "smooth"});
            }
        }, 80);
        return () => window.clearTimeout(timer);
    }, [keyboard.open]);

    return keyboard;
};
